import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';

import { ListTeamSongsQuery, useListTeamSongsQuery } from '@/graphql';
import {
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	readProviderCacheUpdatedAt,
	writeProviderCache,
} from '@/helpers/providerCache';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

import { LoadingResource, useLoading } from '../LoadingProvider';

export type LimitedTeamSong = ListTeamSongsQuery['listMyTeamSongs'][0];

export type LimitedTeamSongs = (LimitedTeamSong | undefined)[];

export interface TeamSongsContext {
	isLoading: boolean;
	isRefetching: boolean;
	error: Error | null;
	teamSongs: ListTeamSongsQuery['listMyTeamSongs'];
	dataUpdatedAt: number;
	refetch: () => void;
	findTeamSongsById: (
		teamSongIds: (string | undefined)[],
	) => LimitedTeamSongs;
	findTeamSongsByIdReduced: (
		teamSongIds: (string | undefined)[],
	) => LimitedTeamSong[];
}

const defaultValue: TeamSongsContext = {
	error: null,
	isLoading: false,
	isRefetching: false,
	teamSongs: [],
	dataUpdatedAt: 0,
	refetch: () => null,
	findTeamSongsById: () => [],
	findTeamSongsByIdReduced: () => [],
};

const TeamSongsContext = createContext<TeamSongsContext>(defaultValue);

export function useTeamSongs(): TeamSongsContext {
	const context = useContext(TeamSongsContext);
	if (isNil(context)) {
		throw new Error(`useTeamSongs must be used within a TeamSongsProvider`);
	}

	return context;
}

export const TeamSongsProvider: React.FC = ({ children }) => {
	const { setLoaded } = useLoading();
	const { enqueueSnackbar } = useSnackbar();
	const cachedTeamSongs = readProviderCache<ListTeamSongsQuery>(
		PROVIDER_CACHE_KEYS.teamSongs,
	);
	const { data, isLoading, error, refetch, dataUpdatedAt, isRefetching } =
		useListTeamSongsQuery<ListTeamSongsQuery, Error>(
			{},
			{
				initialData: cachedTeamSongs ?? undefined,
				initialDataUpdatedAt: readProviderCacheUpdatedAt(
					PROVIDER_CACHE_KEYS.teamSongs,
				),
				staleTime: 1000 * 60 * 5, // 5 minutes
				refetchOnMount: true,
				refetchOnWindowFocus: true,
			},
		);

	const teamSongs = useMemo(() => {
		if (!data) {
			return [];
		}
		return data?.listMyTeamSongs || [];
		// We need to re-calculate this memo when the data refreshes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dataUpdatedAt]);

	useEffect(() => {
		if (!isLoading) {
			setLoaded(LoadingResource.TEAM_SONGS);
		}
	}, [isLoading, setLoaded]);

	useEffect(() => {
		if (data) {
			writeProviderCache(PROVIDER_CACHE_KEYS.teamSongs, data);
		}
	}, [data]);

	const findTeamSongsById = useCallback(
		(teamSongIds: (string | undefined)[]): LimitedTeamSongs => {
			return teamSongIds.map((tsi) => {
				return teamSongs.find((ts) => ts.id === tsi);
			});
		},
		[teamSongs],
	);

	const findTeamSongsByIdReduced = useCallback(
		(teamSongIds: (string | undefined)[]): LimitedTeamSong[] => {
			return teamSongIds.reduce<LimitedTeamSong[]>((acc, tsi) => {
				const teamSong = teamSongs.find((ts) => ts.id === tsi);
				if (teamSong) {
					return [...acc, teamSong];
				}
				return acc;
			}, []);
		},
		[teamSongs],
	);

	if (error) {
		enqueueSnackbar('Failed to load team songs', {
			variant: 'warning',
		});
	}

	return (
		<TeamSongsContext.Provider
			value={{
				isLoading,
				isRefetching,
				teamSongs,
				error,
				dataUpdatedAt,
				refetch,
				findTeamSongsById,
				findTeamSongsByIdReduced,
			}}
		>
			{children}
		</TeamSongsContext.Provider>
	);
};

export default TeamSongsProvider;
